import { useEffect, useRef, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { searchNutrition, type NutritionSearchResult, type PlanEditorItem } from '../lib/api';

interface Props {
  item: PlanEditorItem;
  index: number;
  onChange: (patch: Partial<PlanEditorItem>) => void;
  onRemove: () => void;
  disabled?: boolean;
}

/**
 * Sprint 9.B — Autocomplete CREA per ingredienti.
 * Debounce 250ms su /api/nutrition/search; suggerimento mostra kcal/100g;
 * pick popola anche calories hint (computato grossolanamente client-side).
 *
 * Drag&drop handle via prop fornito esternamente. Qui solo input field + popup.
 */
export function IngredientAutocomplete({ item, index, onChange, onRemove, disabled }: Props) {
  const [results, setResults] = useState<NutritionSearchResult[]>([]);
  const [showPopup, setShowPopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current);
    }
    const q = item.ingredient.trim();
    if (q.length < 2 || !showPopup) {
      setResults([]);
      return;
    }
    debounceRef.current = window.setTimeout(() => {
      setLoading(true);
      void (async () => {
        try {
          const data = await searchNutrition(q, 8);
          setResults(data.results);
        } catch {
          setResults([]);
        } finally {
          setLoading(false);
        }
      })();
    }, 250);

    return () => {
      if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    };
  }, [item.ingredient, showPopup]);

  // Close popup on outside click
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowPopup(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (r: NutritionSearchResult) => {
    // Calcola kcal stimato se quantity + g
    let kcal: number | null = null;
    const qty = item.quantity ? parseFloat(item.quantity.replace(',', '.')) : NaN;
    if (!Number.isNaN(qty) && (item.unit === 'g' || item.unit === null || item.unit === 'ml')) {
      kcal = Math.round((r.kcal_per_100g * qty) / 100);
    }
    onChange({
      ingredient: r.name,
      calories: kcal,
    });
    setShowPopup(false);
    setResults([]);
  };

  return (
    <div
      ref={containerRef}
      style={{
        // Colonne flessibili: il nome (minmax 0) può restringersi sotto la larghezza del
        // contenuto, q.tà/unità/cestino compatti → niente overflow nelle colonne strette
        // del board (min totale ~120px invece di ~350px). Fix 2026-06-26 "info escono fuori".
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 52px 34px 30px',
        gap: 5,
        alignItems: 'start',
        position: 'relative',
        marginBottom: 6,
      }}
    >
      <div style={{ position: 'relative', minWidth: 0 }}>
        <input
          type="text"
          value={item.ingredient}
          onChange={(e) => onChange({ ingredient: e.target.value })}
          onFocus={() => setShowPopup(true)}
          placeholder="Ingrediente"
          disabled={disabled}
          aria-label={`Ingrediente ${index + 1}`}
          title={item.ingredient || undefined}
          style={inputStyle}
        />
        {showPopup && (results.length > 0 || loading) && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: 2,
              background: 'var(--surface)',
              border: '1px solid var(--line)',
              borderRadius: 8,
              boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
              zIndex: 50,
              maxHeight: 220,
              overflowY: 'auto',
            }}
          >
            {loading && (
              <div style={{ padding: 10, fontSize: 12, color: 'var(--mute)' }}>Cerco…</div>
            )}
            {!loading &&
              results.map((r) => (
                <button
                  type="button"
                  key={r.food_code}
                  onClick={() => pick(r)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '7px 10px',
                    border: 0,
                    background: 'transparent',
                    cursor: 'pointer',
                    fontSize: 12.5,
                    color: 'var(--ink)',
                    borderBottom: '1px solid var(--line-3)',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-2)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <div style={{ fontWeight: 600 }}>{r.name}</div>
                  <div style={{ fontSize: 10.5, color: 'var(--mute)', fontFamily: 'var(--mono)', marginTop: 2 }}>
                    {Math.round(r.kcal_per_100g)} kcal/100g
                  </div>
                </button>
              ))}
          </div>
        )}
      </div>
      <input
        type="text"
        value={item.quantity ?? ''}
        onChange={(e) => onChange({ quantity: e.target.value || null })}
        placeholder="Q.tà"
        disabled={disabled}
        aria-label="Quantità"
        inputMode="decimal"
        style={inputStyle}
      />
      <input
        type="text"
        value={item.unit ?? ''}
        onChange={(e) => onChange({ unit: e.target.value || null })}
        placeholder="g"
        disabled={disabled}
        aria-label="Unità"
        style={inputStyle}
      />
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        aria-label="Rimuovi ingrediente"
        style={{
          border: '1px solid var(--line)',
          background: 'var(--surface)',
          color: 'var(--red)',
          padding: 6,
          borderRadius: 6,
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Trash2 size={13} />
      </button>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  border: '1px solid var(--line)',
  borderRadius: 6,
  padding: '7px 9px',
  fontSize: 13,
  background: 'var(--surface)',
  color: 'var(--ink)',
  fontFamily: 'var(--sans)',
  boxSizing: 'border-box',
};
